import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit3, Trash2, Loader2, Printer } from "lucide-react";
import { Pill } from "@/components/layout/PageShell";
import MasterCrudShell from "@/components/layout/MasterCrudShell";
import { DataToolbar, DataTable } from "@/components/layout/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { toast } from "sonner";
import { QrMenuCardPrint, printQrMenuCards } from "@/components/qr/QrMenuCardPrint";

const AREAS = ["Main Hall", "Family Area", "VIP Area", "Terrace", "Outdoor"];
const TABLE_SIZE = 90;
const STATUSES = ["Vacant", "Occupied", "Printed", "Multi", "Inactive"];

const EMPTY = {
  code: "",
  name: "",
  area: "Main Hall",
  shape: "square",
  pos_x: 0,
  pos_y: 0,
  width: TABLE_SIZE,
  height: TABLE_SIZE,
  capacity: 4,
  status: "Vacant",
  sort_order: 0,
  is_active: true,
};

export default function TablesMaster() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const branchId = user?.default_branch?.id;
  const branchCode = user?.default_branch?.code;
  const branchName = user?.default_branch?.name || "Restaurant";
  const [view, setView] = useState("form");
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const { data: tables = [], isLoading } = useQuery({
    queryKey: ["tables", branchId],
    queryFn: async () => {
      const { data } = await api.get("/tables", { params: { branch_id: branchId } });
      return data.tables;
    },
    enabled: Boolean(branchId),
  });

  const { data: restSettings } = useQuery({
    queryKey: ["restaurant-settings", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/restaurant-settings", { params: { branch_id: branchId } });
      return res.settings;
    },
    enabled: Boolean(branchId),
  });

  const filtered = tables.filter(
    (row) =>
      row.name.toLowerCase().includes(search.toLowerCase()) ||
      row.code.toLowerCase().includes(search.toLowerCase()) ||
      row.area.toLowerCase().includes(search.toLowerCase()),
  );

  const printCardCount = useMemo(() => {
    return filtered.filter((row) => row.is_active !== false).length || 0;
  }, [filtered]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        code: form.code,
        name: form.name,
        area: form.area,
        shape: "square",
        pos_x: Number(form.pos_x),
        pos_y: Number(form.pos_y),
        width: TABLE_SIZE,
        height: TABLE_SIZE,
        capacity: Number(form.capacity),
        status: form.status,
        sort_order: Number(form.sort_order),
        is_active: form.is_active,
      };
      if (editingId) {
        return api.put(`/tables/${editingId}`, payload, { params: { branch_id: branchId } });
      }
      return api.post("/tables", payload, { params: { branch_id: branchId } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tables", branchId] });
      toast.success(editingId ? "Table updated" : "Table created");
      setEditingId(null);
      setForm(EMPTY);
      setView("form");
    },
    onError: () => toast.error("Failed to save table"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/tables/${id}`, { params: { branch_id: branchId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tables", branchId] });
      toast.success("Table deleted");
    },
    onError: () => toast.error("Failed to delete table"),
  });

  function openNew() {
    setEditingId(null);
    setForm({ ...EMPTY, code: `T-${String(tables.length + 1).padStart(2, "0")}` });
  }

  function openEdit(row) {
    setEditingId(row.id);
    setForm({
      code: row.code,
      name: row.name,
      area: row.area,
      shape: "square",
      pos_x: row.x,
      pos_y: row.y,
      width: TABLE_SIZE,
      height: TABLE_SIZE,
      capacity: row.capacity,
      status: row.status,
      sort_order: row.sort_order,
      is_active: row.is_active,
    });
    setView("form");
  }

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handlePrintCards() {
    if (!branchCode) {
      toast.error(t("qrMenu.noBranch"));
      return;
    }
    if (printCardCount === 0) {
      toast.error(t("qrMenu.noTablesToPrint"));
      return;
    }
    if (!restSettings?.qr_menu_enabled) {
      toast.message(t("qrMenu.enableBeforePrint"));
    }
    printQrMenuCards();
  }

  function handleSave() {
    if (!form.code?.trim() || !form.name?.trim()) {
      toast.error("Code and display name are required.");
      return;
    }
    saveMutation.mutate();
  }

  const statusTone = (v) => {
    if (v === "Vacant") return "green";
    if (v === "Occupied") return "red";
    if (v === "Printed") return "amber";
    if (v === "Multi") return "blue";
    return "default";
  };

  const printButton = (
    <Button
      variant="outline"
      size="sm"
      className="h-9"
      onClick={handlePrintCards}
      disabled={!branchCode || printCardCount === 0}
    >
      <Printer size={14} className="mr-1.5" />
      {t("qrMenu.printTableCards")}
    </Button>
  );

  return (
    <>
      <MasterCrudShell
        title="Table Master"
        subtitle={`${tables.length} tables — ${user?.default_branch?.name || "branch"}`}
        view={view}
        onViewChange={setView}
        isNew={!editingId}
        onSave={handleSave}
        saving={saveMutation.isPending}
        onAddNew={openNew}
        testIdPrefix="tbm"
        formTitle={editingId ? form.name || form.code || "Edit Table" : "New Table"}
        formSubtitle={editingId ? "Edit" : "Create"}
        formExtra={printButton}
        list={
          <>
            <DataToolbar
              searchValue={search}
              onSearch={setSearch}
              testIdPrefix="tbm"
              extra={printButton}
            />
            {isLoading ? (
              <div className="h-32 grid place-items-center text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : (
              <DataTable
                testIdPrefix="tbm"
                onRowClick={openEdit}
                columns={[
                  { key: "code", label: "Code", mono: true, bold: true },
                  { key: "name", label: "Display Name" },
                  { key: "area", label: "Area" },
                  { key: "capacity", label: "Seats", align: "right" },
                  {
                    key: "status",
                    label: "Status",
                    render: (v) => <Pill tone={statusTone(v)}>{v}</Pill>,
                  },
                ]}
                rows={filtered}
                rowActions={(row) => [
                  { title: "Edit", icon: <Edit3 size={14} />, onClick: () => openEdit(row) },
                  {
                    title: "Delete",
                    icon: <Trash2 size={14} />,
                    danger: true,
                    onClick: () => {
                      if (window.confirm(`Delete table ${row.code}?`)) {
                        deleteMutation.mutate(row.id);
                      }
                    },
                  },
                ]}
              />
            )}
          </>
        }
      >
        <div className="grid grid-cols-2 gap-3 max-w-lg">
          <Field label="Code *">
            <Input
              value={form.code}
              onChange={(e) => setField("code", e.target.value)}
              className="h-9 font-mono"
              disabled={Boolean(editingId)}
              autoFocus={!editingId}
            />
          </Field>
          <Field label="Display Name *">
            <Input
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              className="h-9"
              autoFocus={Boolean(editingId)}
            />
          </Field>
          <Field label="Area">
            <Select value={form.area} onValueChange={(v) => setField("area", v)}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {AREAS.map((a) => (
                  <SelectItem key={a} value={a}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="X Position">
            <Input type="number" value={form.pos_x} onChange={(e) => setField("pos_x", e.target.value)} className="h-9" />
          </Field>
          <Field label="Y Position">
            <Input type="number" value={form.pos_y} onChange={(e) => setField("pos_y", e.target.value)} className="h-9" />
          </Field>
          <Field label="Seats">
            <Input type="number" value={form.capacity} onChange={(e) => setField("capacity", e.target.value)} className="h-9" />
          </Field>
          <Field label="Sort Order">
            <Input type="number" value={form.sort_order} onChange={(e) => setField("sort_order", e.target.value)} className="h-9" />
          </Field>
          <div className="col-span-2">
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setField("status", v)}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
        </div>
      </MasterCrudShell>

      {branchCode ? (
        <QrMenuCardPrint
          restaurantName={branchName}
          tagline={restSettings?.qr_menu_tagline}
          theme={restSettings?.qr_menu_theme || "warm_spice"}
          logoUrl={restSettings?.qr_menu_logo_url || undefined}
          branchCode={branchCode}
          count={printCardCount || 1}
        />
      ) : null}
    </>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[12px] text-slate-600 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
